import { useEffect, useId, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Wordmark from '../components/site/Wordmark'
import { getInactivityTimeoutMs, isInactive, recordActivity, signOutForInactivity, takeInactivityNotice } from '../lib/inactivity'
import { hasSupabaseConfig, missingSupabaseConfigMessage, supabase } from '../lib/supabase'

/** Where each role lands after sign-in. */
const ROLE_ROUTE: Partial<Record<string, string>> = {
  agent: '/agent',
  supervisor: '/supervisor',
  administrator: '/admin',
  leadership: '/leadership',
}

type RoleOutcome = { route: string } | { notice: string }

/**
 * Shared by a fresh sign-in and by the persisted-session check on mount, so
 * the two paths cannot drift into reporting a role differently.
 */
async function resolveRoleOutcome(userId: string): Promise<RoleOutcome> {
  const { data: profile, error } = await supabase
    .from('profiles')
    .select('role')
    .eq('id', userId)
    .single()

  // PGRST116 is `.single()` reporting zero rows, the expected shape for an
  // account with no profile yet. Any other error is a real failure to reach
  // the database, which is not the same thing and should not be told to the
  // account as "no role is assigned".
  if (error && error.code !== 'PGRST116') {
    return { notice: 'Something went wrong reaching your account. Try again.' }
  }

  if (!profile) {
    return { notice: 'Signed in, but no role is assigned yet. Contact an administrator.' }
  }

  const route = ROLE_ROUTE[profile.role]
  if (!route) {
    return { notice: `Signed in as ${profile.role}, but there is no workspace for that role. Contact an administrator.` }
  }

  return { route }
}

/**
 * Sign in, through Supabase Auth, and the home page: MUSTER has nothing for
 * a signed-out visitor to do but sign in, so "/" renders this rather than a
 * marketing page nobody in a field office needs.
 *
 * The flow is real: a configured build signs in against the project's own auth
 * service. The template ships without credentials, so `hasSupabaseConfig` is
 * false and the form says so before anything is typed rather than failing at
 * the network afterwards.
 *
 * Where a signed-in account lands depends on its role, read from `profiles`
 * (docs/product/personas.md: assigned administratively, never chosen at
 * sign-in). A role with no workspace built yet, or an account with no
 * profile row, says so rather than redirecting somewhere misleading.
 *
 * Every field carries a real label rather than a placeholder standing in for
 * one: placeholder text disappears on focus, which leaves a screen reader user
 * and anyone who looked away without a label.
 */
export default function LoginPage() {
  const emailId = useId()
  const passwordId = useId()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [notice, setNotice] = useState(() => takeInactivityNotice())
  const [busy, setBusy] = useState(false)
  const navigate = useNavigate()

  // A returning visitor who is already signed in, opening "/" in a new tab or
  // from a bookmark, has a persisted session before this form ever submits.
  // Without this, they would see the sign-in form again and have to know the
  // workspace URL, despite the session Supabase already refreshed for them.
  useEffect(() => {
    if (!hasSupabaseConfig) return
    let cancelled = false

    async function checkSession() {
      const {
        data: { session },
      } = await supabase.auth.getSession()
      if (!session || cancelled) return

      // A session persisted from a long absence is ended here rather than
      // resumed into the workspace.
      if (isInactive(await getInactivityTimeoutMs())) {
        await signOutForInactivity()
        if (!cancelled) setNotice(takeInactivityNotice())
        return
      }
      if (cancelled) return

      const outcome = await resolveRoleOutcome(session.user.id)
      if (cancelled) return

      if ('route' in outcome) {
        navigate(outcome.route, { replace: true })
      } else {
        setNotice(outcome.notice)
      }
    }

    void checkSession()
    return () => {
      cancelled = true
    }
  }, [navigate])

  async function signIn(event: React.FormEvent) {
    event.preventDefault()
    if (!hasSupabaseConfig) {
      setNotice(missingSupabaseConfigMessage)
      return
    }

    setBusy(true)
    setNotice('')
    const { data, error } = await supabase.auth.signInWithPassword({ email, password })

    // One message for a wrong address and a wrong password alike. Telling the
    // two apart confirms which addresses have accounts, which is how a list of
    // addresses gets tested against a service.
    if (error) {
      setBusy(false)
      setNotice('Those details were not recognised.')
      return
    }

    recordActivity()
    const outcome = await resolveRoleOutcome(data.user.id)
    setBusy(false)

    if ('notice' in outcome) {
      setNotice(outcome.notice)
      return
    }
    navigate(outcome.route)
  }

  async function resetPassword() {
    if (!hasSupabaseConfig) {
      setNotice(missingSupabaseConfigMessage)
      return
    }
    if (!email) {
      setNotice('Enter your email address first.')
      return
    }

    await supabase.auth.resetPasswordForEmail(email)
    // Reported the same way whether or not an account exists, for the same
    // reason the sign-in message is uniform.
    setNotice('If that address has an account, a reset link is on its way.')
  }

  return (
    <div className="page-shell py-10 sm:py-16">
      <div className="mx-auto max-w-md">
        <Wordmark className="mx-auto h-auto w-72 max-w-full" />
        <h1 className="mt-6 text-center text-3xl font-black tracking-tight">Sign in</h1>

        {!hasSupabaseConfig && (
          <div className="mt-6 rounded-lg border border-line bg-surface p-4 text-sm">
            <p className="font-semibold">Sign-in is unavailable</p>
            <p className="mt-1 text-ink-muted">
              {missingSupabaseConfigMessage}
            </p>
          </div>
        )}

        <form className="mt-6 flex flex-col gap-4" onSubmit={signIn}>
          <div>
            <label htmlFor={emailId} className="block text-sm font-semibold">
              Email address
            </label>
            <input
              id={emailId}
              type="email"
              name="email"
              autoComplete="username"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              className="mt-1 w-full rounded border border-line bg-canvas px-3 py-2"
            />
          </div>

          <div>
            <div className="flex items-baseline justify-between gap-4">
              <label htmlFor={passwordId} className="block text-sm font-semibold">
                Password
              </label>
              <button
                type="button"
                onClick={resetPassword}
                className="text-sm text-accent-text underline-offset-2 hover:underline"
              >
                Forgot your password?
              </button>
            </div>
            <input
              id={passwordId}
              type="password"
              name="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              className="mt-1 w-full rounded border border-line bg-canvas px-3 py-2"
            />
          </div>

          <button
            type="submit"
            disabled={busy}
            className="rounded bg-primary px-4 py-2 text-base font-bold text-primary-ink hover:bg-primary-hover disabled:opacity-60"
          >
            {busy ? 'Signing in' : 'Sign in'}
          </button>

          {/* Announced rather than only shown, since submitting is the only
              thing that reveals it. */}
          <p role="status" aria-live="polite" className="min-h-5 text-sm text-ink-muted">
            {notice}
          </p>
        </form>
      </div>
    </div>
  )
}
